import { IconName } from '#components';
import { GenericLinkComponent } from '#components/TextLink/TextLink';
import { ReactNode } from 'react';

export enum ButtonSizes {
  M = 'medium',
  S = 'small',
  COMPACT = 'compact',
}
export type ButtonSize = 'medium' | 'small' | 'compact';

export enum ButtonVariants {
  TEXT = 'text',
  SOLID = 'solid',
  WRAPPER = 'wrapper',
  LINK = 'link',
}
export type ButtonVariant = 'text' | 'solid' | 'wrapper' | 'link';
export type DeprecatedButtonVariant = 'outline' | 'transparent' | 'tertiary';
export type ButtonIconSize = 'xs' | 's';

export enum ButtonColors {
  // Reds
  LIGHT_RED = 'lightRed',
  RED = 'red',
  DARK_RED = 'darkRed',
  // Oranges
  LIGHT_ORANGE = 'lightOrange',
  ORANGE = 'orange',
  DARK_ORANGE = 'darkOrange',
  // Yellows
  LIGHT_YELLOW = 'lightYellow',
  YELLOW = 'yellow',
  MEDIUM_YELLOW = 'mediumYellow',
  DARK_YELLOW = 'darkYellow',
  // Greens
  LIGHT_GREEN = 'lightGreen',
  GREEN = 'green',
  DARK_GREEN = 'darkGreen',
  // Blues
  LIGHT_BLUE = 'lightBlue',
  BLUE = 'blue',
  DARK_BLUE = 'darkBlue',
  // Purples
  LIGHT_PURPLE = 'lightPurple',
  PURPLE = 'purple',
  DARK_PURPLE = 'darkPurple',
  // Neutrals
  WHITE = 'white',
  LIGHT = 'light',
  GREY = 'grey',
  DARK = 'dark',
}
export type ButtonColor =
  // Reds
  | 'lightRed'
  | 'red'
  | 'darkRed'
  // Oranges
  | 'lightOrange'
  | 'orange'
  | 'darkOrange'
  // Yellows
  | 'lightYellow'
  | 'yellow'
  | 'mediumYellow'
  | 'darkYellow'
  // Greens
  | 'lightGreen'
  | 'green'
  | 'darkGreen'
  // Blues
  | 'lightBlue'
  | 'blue'
  | 'darkBlue'
  // Purples
  | 'lightPurple'
  | 'purple'
  | 'darkPurple'
  // Neutrals
  | 'white'
  | 'light'
  | 'grey'
  | 'dark';

// Palette tokens — strictly mirrors the CSS variables defined in
// packages/ui/src/styles/palette.css. Adding a new shade here without
// adding the matching `--<token>` variable will silently render `transparent`.
export type PaletteColor =
  | `warm-grey-${100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900}`
  | `cold-grey-${100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900}`
  | 'white'
  | `green-${100 | 200 | 300 | 400 | 500}`
  | `purple-${100 | 200 | 300 | 400 | 500}`
  | `orange-${100 | 200 | 300 | 400 | 450 | 500}`
  | `red-${100 | 150 | 200 | 300 | 400 | 500}`
  | `blue-${100 | 200 | 300 | 400 | 500 | 600 | 900}`
  | `yellow-${100 | 200 | 300 | 400 | 500 | 900}`;

export interface ButtonProps {
  label?: string;
  size?: ButtonSize;
  variant?: ButtonVariant | DeprecatedButtonVariant;
  type?: 'button' | 'submit' | 'reset';
  form?: string;
  width?: 'content' | 'full';
  color?: ButtonColor | PaletteColor;
  className?: string;
  iconNameLeft?: IconName;
  iconNameRight?: IconName;
  iconSize?: ButtonIconSize;
  fullWidth?: boolean;
  loading?: boolean;
  disabled?: boolean;
  children?: ReactNode;
  onClick?: (() => Promise<void>) | (() => void);
  hrefTracking?: string;
  isExternalTracking?: boolean;
}

export interface ButtonContentProps {
  loading?: boolean;
  iconNameLeft?: IconName;
  iconNameRight?: IconName;
  iconSize?: ButtonIconSize;
  children?: ReactNode | string;
  size?: ButtonSize;
}
export interface LinkButtonProps extends ButtonProps {
  href: string | { pathname: string; search?: string };
  state?: { from?: string; tinyAccountId?: string | null };
  external?: boolean;
  linkClassName?: string;
  NavLinkComponent?: GenericLinkComponent;
  navigate?: (url: string, options?: { replace?: boolean }) => void;
  navigateOptions?: { replace?: boolean };
}
